import styled, { css } from "styled-components";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { FC } from "react";

import Text from "@/components/Text";
import { hexToRgba } from "@/utils/theme";

const Card = styled.div<{ $isReversed: boolean }>`
  background: linear-gradient(
    106.25deg,
    rgba(65, 54, 207, 0.9) 0%,
    rgba(93, 84, 214, 0.8) 100%
  );
  padding: 72px 70px 95px 48px;
  display: flex;
  position: relative;
  overflow-y: hidden;
  justify-content: space-between;
  margin-bottom: 48px;
  ${({ $isReversed }) =>
    $isReversed &&
    css`
      padding: 72px 60px 95px 93px;
    `}
`;

const Bubble1 = styled.div`
  width: 133px;
  height: 133px;
  position: absolute;
  background: linear-gradient(
    140.84deg,
    rgba(255, 255, 255, 0.1) 13.86%,
    rgba(255, 255, 255, 0) 83.54%
  );
  left: 6px;
  top: 8px;
  border-radius: 50%;
`;

const Bubble2 = styled.div`
  width: 220px;
  height: 220px;
  position: absolute;
  background: linear-gradient(
    34.64deg,
    rgba(255, 255, 255, 0.2) 16.49%,
    rgba(255, 255, 255, 0) 98.7%
  );
  left: 0;
  top: 8px;
  right: 42px;
  bottom: 0;
  margin: auto;
  border-radius: 50%;
`;

const Bubble3 = styled.div`
  width: 135px;
  height: 135px;
  position: absolute;
  background: linear-gradient(
    53.25deg,
    rgba(255, 255, 255, 0.3) -19.93%,
    rgba(255, 255, 255, 0) 106.56%
  );
  right: 64px;
  bottom: -83px;
  border-radius: 50%;
`;

const CardLeft = styled.div<{ $isReversed: boolean }>`
  width: 51%;
  ${({ $isReversed }) =>
    $isReversed &&
    css`
      order: 1;
      width: 52.91%;
    `}
`;

const CardRight = styled.div`
  width: 37%;
`;

const ImageHolder = styled.div`
  position: relative;
  &:after {
    content: "";
    position: absolute;
    left: 12px;
    top: 12px;
    height: 100%;
    width: 100%;
    background: ${({ theme }) => theme.colors.white};
    opacity: 0.1;
    z-index: 0;
  }
`;

const Image = styled(LazyLoadImage)`
  width: 100%;
  position: relative;
  z-index: 1;
  display: block;
  margin-top: 16px;
`;

const CardHeading = styled(Text)`
  display: flex;
  align-items: flex-start;
  margin-bottom: 40px;
`;

const Count = styled(Text)`
  background: ${({ theme }) => hexToRgba(theme.colors.white, 0.4)};
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 16px;
  transform: translateY(2px);
`;

const Item = styled.li`
  margin-bottom: 48px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemText = styled(Text)`
  line-height: 18px;
  font-weight: 600;
  position: relative;
  padding-left: 24px;
  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    height: 8px;
    width: 8px;
    background: ${({ theme }) => theme.colors.grayscale};
    opacity: 0.5;
  }
`;

interface Props {
  isReversed?: boolean;
  count: number;
  data: {
    title: string;
    image: string;
    list: string[];
  };
}

const HomeProductCard: FC<Props> = ({ isReversed = false, count, data }) => {
  return (
    <Card $isReversed={isReversed}>
      <Bubble1 />
      <Bubble2 />
      <Bubble3 />
      <CardLeft $isReversed={isReversed}>
        <ImageHolder>
          <Image src={data.image} alt={data.title} />
        </ImageHolder>
      </CardLeft>
      <CardRight>
        <CardHeading as="h3" $color="white" $type="heading2">
          <Count $color="white" $type="smallBold" as="span">
            {count}
          </Count>
          {data.title}
        </CardHeading>
        <ul>
          {data.list.map((text, index) => (
            <Item key={index}>
              <ItemText $color="white" $type="smallBold">
                {text}
              </ItemText>
            </Item>
          ))}
        </ul>
      </CardRight>
    </Card>
  );
};

export default HomeProductCard;
